import useI18n from "@/hooks/useI18n";
import { DurationIcons } from "@/appContent/pricing";
import { css, cx } from "@styled-system/css";
import { useRouter } from "@/router";
import { Flex } from "@styled-system/jsx";
import environment from "@/modules/environment";
import forUserForGuest from "@/utils/forUserForGuest";
import { button } from "@styled-system/recipes";
import Badge from "./Badge";
import { shapeComponent } from "../utils/shapeComponent";

type Props = Pick<Product, "price" | "trialDuration" | "currency" | "productId" | "couponCode" | "listPrice"> & {
  buttonLabel: string;
  save?: number | boolean;
  countryCode: string;
  index: number;
  period: "week" | "month" | "year";
  trialPeriod: "day" | "week" | "month";
};

export default function ButtonCard({
  price,
  buttonLabel,
  currency,
  save,
  period,
  trialPeriod,
  trialDuration,
  countryCode,
  productId,
  couponCode,
  listPrice,
  index,
  ...rest
}: Props) {
  const { t } = useI18n();
  const isTrial = Boolean(trialPeriod || trialDuration);
  const { query, push } = useRouter();
  const DurationIcon = DurationIcons[period];
  const { isCamerito } = environment;

  const headingContent =
    isTrial && trialPeriod
      ? t(`pricing.plurals.${trialPeriod}Trial`, {
          plural: true,
          values: {
            count: trialDuration
          }
        })
      : `${price} ${currency} / ${t(`pricing.${period}`)}`;

  const fromApp = query.from === "webapp";

  const redirectToProduct = () => {
    push(
      `${forUserForGuest({
        user: "/checkout",
        guest: query.preferLogin || isCamerito ? "/login" : "/signup"
      })}?countryCode=${countryCode}&productId=${productId}&from=purchase${couponCode ? `&code=${couponCode}` : ""}`
    );
  };

  return (
    <Flex px={fromApp ? "1rem" : "0 !important"} w={["100%", null, "33.3333%"]}>
      <Flex
        className={css({
          rowGap: "1.25rem",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
          position: "relative",
          borderBottomLeftRadius: !fromApp && index === 0 ? [0, null, "1.5rem"] : index === 2 ? ["1.5rem", null, 0] : 0,
          borderBottomRightRadius: !fromApp && index === 2 ? "1.5rem" : 0,
          borderLeft: index === 1 ? ["none", null, `1px solid`] : "none",
          borderRight: index === 1 ? ["none", null, `1px solid`] : "none",
          borderBottom: index !== 2 ? [`1px solid`, null, null, "none"] : "none",
          borderRadius: fromApp ? "1.5rem !important" : 0,
          border: fromApp ? `1px solid !important` : "none",
          borderColor: "primaryTransparent !important",
          bg: "lightBackground",
          justifyContent: "flex-end",
          padding: "1.5rem",
          pt: save ? "2rem" : "1.5rem"
        })}
        {...rest}
      >
        {save && (
          <Flex
            className={css({
              position: "absolute",
              right: 0,
              top: 0,
              bg: "warning",
              borderRadius: fromApp ? "0 1.5rem 0 1rem" : "0 0 0 1rem",
              p: "0.5rem 0.5rem 0.5rem 1rem"
            })}
          >
            <Badge save={save} fromApp={fromApp} />
          </Flex>
        )}

        <Flex flexDir="column" alignItems="center" mb={isTrial ? "0" : "1.125rem"}>
          {DurationIcon && <DurationIcon className={css({ mt: save ? "0.5rem" : "1.125rem" })} />}
          {period && (
            <p
              className={css({
                textStyle: "overline",
                mt: "1.25rem",
                mb: "0.375rem",
                textAlign: "center",
                color: "lowEmphasis"
              })}
            >
              {t(`pricing.${period}ly`).toUpperCase()}
            </p>
          )}
          {price != null && currency && (
            <Flex flexDir="column" textAlign="center">
              {fromApp ? (
                <h4 className={css({ m: 0 })}>{headingContent}</h4>
              ) : (
                <h5 className={css({ m: 0 })}>{headingContent}</h5>
              )}
              {isTrial && (
                <p>
                  {shapeComponent(
                    {
                      forTrial: `${t("pricing.then")} ${price} ${currency} ${t(`pricing.p${period}`)}`,
                      forDefault: t(`pricing.p${period}`)
                    },
                    isTrial,
                    save
                  )}
                </p>
              )}
            </Flex>
          )}
        </Flex>

        <button
          onClick={redirectToProduct}
          id={`buy-${period}`}
          className={cx(
            button({ variant: isTrial ? "filled" : "outlined" }),
            css({ py: "1.25rem !important", fontSize: "1rem !important" })
          )}
        >
          {buttonLabel}
        </button>
        {isTrial && (
          <p
            className={css({
              fontSize: "1rem",
              pos: "absolute",
              bottom: 0,
              w: "100%",
              transform: "translateY(120%)",
              textAlign: "center",
              color: "lowEmphasis"
            })}
          >
            {t("pricing.explainer")}
          </p>
        )}
      </Flex>
    </Flex>
  );
}
